import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Separate axios instance for tenant portal
const tenantApi = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

tenantApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('tenant_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

tenantApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('tenant_token');
      localStorage.removeItem('tenant_user');
      window.location.href = '/mieter/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
};

// Properties API
export const propertiesAPI = {
  getAll: () => api.get('/properties'),
  getOne: (id) => api.get(`/properties/${id}`),
  create: (data) => api.post('/properties', data),
  update: (id, data) => api.put(`/properties/${id}`, data),
  delete: (id) => api.delete(`/properties/${id}`),
};

// Landlords API
export const landlordsAPI = {
  getAll: () => api.get('/landlords'),
  getOne: (id) => api.get(`/landlords/${id}`),
  create: (data) => api.post('/landlords', data),
  update: (id, data) => api.put(`/landlords/${id}`, data),
  delete: (id) => api.delete(`/landlords/${id}`),
};

// Units API
export const unitsAPI = {
  getAll: (propertyId) => api.get('/units', { params: { property_id: propertyId } }),
  getOne: (id) => api.get(`/units/${id}`),
  create: (data) => api.post('/units', data),
  update: (id, data) => api.put(`/units/${id}`, data),
  delete: (id) => api.delete(`/units/${id}`),
};

// Tenants API
export const tenantsAPI = {
  getAll: (unitId) => api.get('/tenants', { params: { unit_id: unitId } }),
  getOne: (id) => api.get(`/tenants/${id}`),
  create: (data) => api.post('/tenants', data),
  update: (id, data) => api.put(`/tenants/${id}`, data),
  delete: (id) => api.delete(`/tenants/${id}`),
};

// Costs API
export const costsAPI = {
  getAll: (propertyId) => api.get('/costs', { params: { property_id: propertyId } }),
  getOne: (id) => api.get(`/costs/${id}`),
  create: (data) => api.post('/costs', data),
  update: (id, data) => api.put(`/costs/${id}`, data),
  delete: (id) => api.delete(`/costs/${id}`),
  listFiles: (costId) => api.get(`/costs/${costId}/files`),
  uploadFile: (costId, formData) => api.post(`/costs/${costId}/files`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  downloadFile: (fileId) => api.get(`/cost-files/${fileId}/download`, { responseType: 'blob' }),
  deleteFile: (fileId) => api.delete(`/cost-files/${fileId}`),
};

// Billings API
export const billingsAPI = {
  getAll: (propertyId) => api.get('/billings', { params: { property_id: propertyId } }),
  getOne: (id) => api.get(`/billings/${id}`),
  create: (data) => api.post('/billings', data),
  finalize: (id) => api.put(`/billings/${id}/finalize`),
  delete: (id) => api.delete(`/billings/${id}`),
  downloadPdf: (id) => api.get(`/billings/${id}/pdf`, { responseType: 'blob' }),
  sendEmail: (id) => api.post(`/billings/${id}/send-email`),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

// Reference Data API
export const referenceAPI = {
  getCostTypes: () => api.get('/cost-types'),
  getDistributionKeys: () => api.get('/distribution-keys'),
  getUnitTypes: () => api.get('/unit-types'),
  getDamageCategories: () => api.get('/damage-categories'),
  getDamagePriorities: () => api.get('/damage-priorities'),
  getMeterTypes: () => api.get('/meter-types'),
  getMeterUnits: () => api.get('/meter-units'),
};

// Meters API
export const metersAPI = {
  getAll: (params) => api.get('/meters', { params }),
  create: (data) => api.post('/meters', data),
  update: (id, data) => api.put(`/meters/${id}`, data),
  delete: (id) => api.delete(`/meters/${id}`),
  listReadings: (meterId) => api.get(`/meters/${meterId}/readings`),
  addReading: (meterId, data) => api.post(`/meters/${meterId}/readings`, data),
  deleteReading: (readingId) => api.delete(`/meter-readings/${readingId}`),
};

// CSV Import API
export const importAPI = {
  unitsTemplate: () => api.get('/import/units/template'),
  tenantsTemplate: () => api.get('/import/tenants/template'),
  previewUnits: (propertyId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/import/units/preview?property_id=${propertyId}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  commitUnits: (data) => api.post('/import/units/commit', data),
  previewTenants: (propertyId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/import/tenants/preview?property_id=${propertyId}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  commitTenants: (data) => api.post('/import/tenants/commit', data),
};

// Damages API
export const damagesAPI = {
  getAll: (params) => api.get('/damages', { params }),
  getOne: (id) => api.get(`/damages/${id}`),
  create: (data) => api.post('/damages', data),
  update: (id, data) => api.put(`/damages/${id}`, data),
  delete: (id) => api.delete(`/damages/${id}`),
  getStats: () => api.get('/dashboard/damage-stats'),
};

// Tenant Users API (for landlords)
export const tenantUsersAPI = {
  getAll: () => api.get('/tenant-users'),
  create: (data) => api.post('/tenant-users', data),
  delete: (id) => api.delete(`/tenant-users/${id}`),
};

// Tenant Portal API (for tenants)
export const tenantPortalAPI = {
  login: (data) => tenantApi.post('/tenant-portal/login', data),
  forgotPassword: (data) => tenantApi.post('/tenant-portal/forgot-password', data),
  resetPassword: (data) => tenantApi.post('/tenant-portal/reset-password', data),
  getMe: () => tenantApi.get('/tenant-portal/me'),
  getBillings: () => tenantApi.get('/tenant-portal/billings'),
  downloadBillingPdf: (id) => tenantApi.get(`/tenant-portal/billings/${id}/pdf`, { responseType: 'blob' }),
  getDamages: () => tenantApi.get('/tenant-portal/damages'),
  createDamage: (data) => tenantApi.post('/tenant-portal/damages', data),
};

export default api;
